import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { getPath } from '~/utils/routes';
import {
  dateFormat,
  scannerPropType,
  scannerRunPropType
} from '~/components/AgentScanners/types/commonTypes';
import LoadingElement from '~/components/Layout/LoadingElement';
import FormattedDate from '~/components/Layout/FormattedDate';
import Overview from './Overview';
import Review from './Review';
import styles from './ScanAuditLog.css';

const ScanAuditLog = ({
  scanner,
  scannerId,
  scanAuditLogId,
  organizationId,
  scannerRun,
  isFetchingScannerRuns,
  stagingAgents,
  isFetchingStagingAgents,
  loadScannerRuns,
  loadStagingAgents
}) => {
  const history = useHistory();

  // Load scanner runs when component mounts or scanner changes
  React.useEffect(() => {
    if (organizationId && scannerId && scanAuditLogId) {
      loadScannerRuns(organizationId, scannerId);
      loadStagingAgents(organizationId, scannerId, scanAuditLogId);
    }
  }, [
    organizationId,
    scannerId,
    scanAuditLogId,
    loadScannerRuns,
    loadStagingAgents
  ]);

  // Navigate back to the list using getPath
  const handleBackToList = React.useCallback(() => {
    const listPath = getPath('agentScanners');

    history.push(listPath);
  }, [history]);

  // Navigate back to the scanner detail page using scannerId prop
  const handleBackToDetail = React.useCallback(() => {
    // Build detail path directly from scannerId
    const detailPath = `${getPath('agentScanners')}${scannerId}/`;

    history.push(detailPath);
  }, [history, scannerId]);

  return (
    <div id="scanAuditLogPage" data-test-id="scanner-audit-log-page">
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.breadcrumbs}>
          <button
            type="button"
            className={styles.breadcrumbLink}
            onClick={handleBackToList}
            data-test-id="back-to-list"
          >
            Agent scanners
          </button>
          <span className={styles.breadcrumbSeparator}>/</span>
          <button
            type="button"
            className={styles.breadcrumbLink}
            onClick={handleBackToDetail}
            data-test-id="back-to-detail"
          >
            {scanner.name}
          </button>
          <span className={styles.breadcrumbSeparator}>/</span>
          <span
            className={styles.breadcrumbCurrent}
            data-test-id="scanner-name-breadcrumb"
          >
            Scan audit log
          </span>
        </div>
        <div className={styles.detailHeader}>
          <div className={styles.detailTitleSection}>
            <h1
              className={styles.detailTitle}
              data-test-id="scanner-name-heading"
            >
              Scan audit log
              <span display-if={!isFetchingScannerRuns}>
                <span> - </span>
                <FormattedDate
                  testId="scan-config-last-run"
                  date={new Date(scannerRun ? scannerRun.startedAt : '')}
                  format={dateFormat}
                />
              </span>
            </h1>
          </div>
        </div>
      </div>

      <LoadingElement
        message="Loading..."
        data-test-id="loading-element"
        display-if={isFetchingScannerRuns || isFetchingStagingAgents}
      />

      <div
        className={styles.detailContent}
        display-if={!isFetchingScannerRuns && !isFetchingStagingAgents}
      >
        <Overview
          display-if={scanner && scannerRun}
          scannerName={scanner.name}
          connectionName={scanner.connection?.targetSystem?.name || ''}
          scannerRun={scannerRun}
        />
        <Review
          display-if={scanner && stagingAgents}
          stagingAgents={stagingAgents}
          region={scanner.connection.authParameters.region}
        />
      </div>
    </div>
  );
};

ScanAuditLog.propTypes = {
  scanner: scannerPropType,
  scannerId: PropTypes.string,
  scanAuditLogId: PropTypes.string,
  organizationId: PropTypes.string,
  scannerRun: scannerRunPropType,
  isFetchingScannerRuns: PropTypes.bool,
  isFetchingStagingAgents: PropTypes.bool,
  stagingAgents: PropTypes.array,
  loadScannerRuns: PropTypes.func,
  loadStagingAgents: PropTypes.func
};

export default ScanAuditLog;
